import React from "react";
import ContentLayout from "~/components/layouts/content";
import Metadata from "~/components/metadata";
import ListCarContainer from "~/cars/components/CarList/container";
import CarList from "~/cars/components/CarList";
import Loading from "~/cars/components/CarList/loading";
import { useRouter } from "next/router";
import { list } from "~/cars/api/server";
import useCarList from "~/cars/hooks/useCarList";
import routerUtils from "~/common/utils/router";
import FilterBox from "~/cars/components/filter-box";
import GoogleAd from "~/common/components/GoogleAd";
import { ADS } from "~/common/constants/googleAds";

const formatInitialSortValues = (initialQueries) => {
  if (initialQueries.sort_by && initialQueries.order) {
    const inval = `${initialQueries.sort_by} ${initialQueries.order}`;
    return inval;
  }
};

const keysFromUri = [
  "brand",
  "type",
  "transmission",
  "year",
  "model",
  "city",
  "state",
  "lte-year",
  "gte-year",
  "lte-price",
  "gte-price",
];

export default function AutosPage({
  cars: ssCars,
  pagination,
  initialQueries,
}) {
  const router = useRouter();
  const firstLoad = React.useRef(true);

  const { state, onChangeQueries } = useCarList({
    initialCars: ssCars,
    initialPage: routerUtils.getPaginationFromUri(router),
    initialPages: pagination.pages,
    initialFilters: routerUtils.getFiltersFromUri(router, keysFromUri),
  });

  React.useEffect(() => {
    if (!firstLoad.current) {
      const queries = Object.entries(router.query).map((q) => {
        const [key, value] = q;
        return { key, value };
      });
      onChangeQueries(queries);
    } else {
      firstLoad.current = false;
    }
  }, [router.query, onChangeQueries]);

  function handleChangePage(newPage) {
    const newRoute = routerUtils.manageQueries(router, [
      { key: "page", value: newPage },
    ]);
    router.push(newRoute, undefined, { shallow: true });
  }

  function handlePrevPage() {
    const newRoute = routerUtils.manageQueries(router, [
      {
        key: "page",
        value: state.currentPage - 1,
      },
    ]);
    router.push(newRoute, undefined, { shallow: true });
  }

  function handleNextPage() {
    const newRoute = routerUtils.manageQueries(router, [
      {
        key: "page",
        value: state.currentPage + 1,
      },
    ]);
    router.push(newRoute, undefined, { shallow: true });
  }

  function handleApplySort(sortValues) {
    const newRoute = routerUtils.manageQueries(router, [
      {
        key: "sort_by",
        value: sortValues.name,
      },
      {
        key: "order",
        value: sortValues.value,
      },
      {
        key: "page",
        value: 1,
      },
    ]);
    router.push(newRoute, undefined, { shallow: true });
  }

  function handleRemoveQuery(query) {
    const newQuery = routerUtils.removeQueryString(router, query.key);
    router.push(newQuery, undefined, { shallow: true });
  }

  function handleSearch(searchValues) {
    const concateQueries = Object.entries(searchValues).reduce(
      (acc, [key, value]) => {
        if (value !== "all") {
          return [
            ...acc,
            {
              key,
              value,
            },
          ];
        }
        return acc;
      },
      []
    );

    //TODO: Si el valor lte-year y gte-year estan presentes debemos de quitar la query year.
    // Implementar dentro de manageQueries un parametro extra para mandar un arreglo de keys que quiera quitar. Funcionara tambien para los filtros normales porque cuando pase el year quiero quitar el gte-year y el lte-year.
    let keysToRemove = ["page"];
    if (
      concateQueries.find((q) => q.key === "lte-year") &&
      concateQueries.find((q) => q.key === "gte-year")
    ) {
      // keysToRemove = ["year"];
      keysToRemove.push("year");
    }

    const newRoute = routerUtils.manageQueries(
      router,
      concateQueries,
      keysToRemove
    );
    router.push(newRoute, undefined, { shallow: true });
  }

  return (
    <>
      <Metadata
        title="Todos los autos | Seminuevos Sonora"
        description="Listado de todos los autos disponibles"
      />
      <div>
        <ContentLayout>
          <FilterBox onSearch={handleSearch} appliedFilters={state.filters} />
          <ListCarContainer
            queries={state.filters}
            onRemoveQuery={handleRemoveQuery}
            initialSortValue={formatInitialSortValues(initialQueries)}
            onSortApply={handleApplySort}
            currentPage={state.currentPage}
            onPrevClick={handlePrevPage}
            onNextClick={handleNextPage}
            pages={state.pages}
            onChangedPage={handleChangePage}
          >
            {state.isLoading ? (
              <Loading elementsPerRow={3} />
            ) : (
              <CarList elementsPerRow={3} cars={state.cars} withAd />
            )}
          </ListCarContainer>
        </ContentLayout>
        <GoogleAd adSlot={ADS.HORIZONTAL_AD} />
      </div>
    </>
  );
}

export async function getServerSideProps({ query }) {
  try {
    const cars = await list({
      query: {
        ...query,
        gte_year: query["gte-year"],
        lte_year: query["lte-year"],
        gte_price: query["gte-price"],
        lte_price: query["lte-price"],
      },
    });
    const { page, ...rest } = query;
    return {
      // props: cars,
      props: {
        ...cars,
        initialQueries: rest,
      },
    };
  } catch (error) {}
}
